import React from 'react'
import axios from 'axios'
import { Dialog, DialogContent, CircularProgress, TextField } from '@material-ui/core'
import { SourceChip } from './SourceChip'

const CATEGORIES = ['general', 'irrigation', 'plant', 'aggregate', 'lighting', 'hardscape', 'herbicide', 'fertilizer']

const fmt$ = (v) => v != null ? '$' + Number(v).toFixed(2) : ''
const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
const fmtQty = (v) => Number(v || 0).toLocaleString('en-US')

function buildLinkBody (item) {
  switch (item.source) {
    case 'catalog': return { materialCatalogItemId: item.materialCatalogItemId || item._id }
    case 'aspire': return { aspireItemId: item.aspireItemId }
    case 'vendor': return { vendorCatalogProductId: item.vendorCatalogProductId }
    case 'legacy': return { legacyTakeoffId: item.legacyTakeoffId }
    case 'aggregate': return { aggregateMaterialProfileId: item.aggregateMaterialProfileId, aggregateGradationId: item.aggregateGradationId }
    default: return { materialCatalogItemId: item._id }
  }
}

function StripedPlaceholder ({ size = 80, label }) {
  return (
    <div style={{ width: size, height: size, position: 'relative' }}>
      <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`} preserveAspectRatio="none">
        <defs>
          <pattern id="stripes-ph" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(28,36,25,0.10)" strokeWidth="3" />
          </pattern>
        </defs>
        <rect width={size} height={size} fill="url(#stripes-ph)" />
      </svg>
      {label && <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--ink-4)', textAlign: 'center', lineHeight: 1.3, padding: 4 }}>{label}</span>}
    </div>
  )
}

function DetField ({ label, value, mono }) {
  return (
    <div className="det-field">
      <span className="det-label">{label}</span>
      <span className={`det-value${mono ? ' mono' : ''}`}>{value}</span>
    </div>
  )
}

function PriceTier ({ label, value, primary, dim }) {
  return (
    <div className={`price-tier${primary ? ' price-tier--primary' : ''}${dim ? ' price-tier--dim' : ''}`}>
      <span className="det-label">{label}</span>
      <span className="price-val">{fmt$(value)}</span>
    </div>
  )
}

function DialogHeader ({ material, catalogProduct, imgError, setImgError, onClose }) {
  const opt = material?.vendorOptions?.[0]
  const [detOpen, setDetOpen] = React.useState(true)

  const d = catalogProduct || null
  const img = d?.media?.find(m => m.isPrimary)?.url || d?.media?.[0]?.url
  const pricing = d?.pricing
  const hasRichData = !!d

  return (
    <>
      <div className="dlg-head-bar">
        <span className="dlg-overline">Linking material to catalog</span>
        <h3 className="dlg-title">Find a catalog match for this material</h3>
      </div>

      <div className="dlg-source-card">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ marginBottom: 4 }}>
              {material.vendorOptions?.map((vo, i) => (
                <SourceChip key={i} kind="quote" label={vo.vendor} size="xs" />
              ))}
            </div>
            <div className="dlg-source-name">
              {material.canonicalName}
              {d?.externalUrl && <span className="live-pip">● LIVE</span>}
            </div>
            <div className="dlg-source-meta">
              <span>{material.category}</span>
              <span className="dot-sep">·</span>
              <span className="mono">{fmtQty(material.totalQty)} {material.unit}</span>
              {opt && (
                <>
                  <span className="dot-sep">·</span>
                  <span className="mono">{fmt$(opt.unitPrice)}/{opt.uom || material.unit}</span>
                  <span className="dot-sep">·</span>
                  <span className="mono">{opt.vendorSku}</span>
                  <span className="dot-sep">·</span>
                  <span>from <b>{opt.vendor}</b></span>
                </>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--ink-3)', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}
          >×</button>
        </div>
      </div>

      {hasRichData && detOpen && (
        <div className="src-det">
          <div className="src-det-grid">
            <div className="src-det-img">
              {img && !imgError
                ? <img src={img} alt="" onError={() => setImgError(true)} />
                : <StripedPlaceholder size={80} label={`product image\nfrom ${opt?.vendor || ''}`} />
              }
            </div>
            <div className="src-det-body">
              {d.description && <p className="src-det-desc">{d.description}</p>}
              <div className="src-det-fields">
                {d.manufacturer && <DetField label="Manufacturer" value={d.manufacturer} />}
                {d.manufacturerPartNumber && <DetField label="MPN" value={d.manufacturerPartNumber} mono />}
                {d.category && <DetField label="Vendor category" value={cap(d.category)} />}
                {d.vendorItemNumber && <DetField label="Vendor SKU" value={d.vendorItemNumber} mono />}
              </div>
              {pricing && (
                <div className="pricing-tiers pricing-tiers-source">
                  {pricing.ourPrice != null && <PriceTier label="Our price" value={pricing.ourPrice} primary />}
                  {pricing.accountPrice != null && <PriceTier label="Account" value={pricing.accountPrice} />}
                  {pricing.regularPrice != null && <PriceTier label="List" value={pricing.regularPrice} />}
                  {opt?.unitPrice != null && <PriceTier label="On quote" value={opt.unitPrice} dim />}
                </div>
              )}
            </div>
          </div>
          <button className="src-det-collapse" onClick={() => setDetOpen(false)}>▴ Hide details</button>
        </div>
      )}

      {hasRichData && !detOpen && (
        <button className="src-det-reveal" onClick={() => setDetOpen(true)}>
          ▾ Show what we know about this item — {opt?.vendor} catalog
        </button>
      )}
    </>
  )
}

function Group ({ kind, title, subtitle, items, empty, onSelect, linking, showPromote, defaultOpen }) {
  const [open, setOpen] = React.useState(defaultOpen != null ? defaultOpen : (items && items.length > 0))
  const count = items?.length || 0

  return (
    <div className="grp-block">
      <button className="grp-head" onClick={() => setOpen(!open)}>
        <span className="grp-chev">{open ? '▾' : '▸'}</span>
        <SourceChip kind={kind} size="xs" />
        <span className="grp-title">{title}</span>
        <span className="grp-sub">{subtitle}</span>
        <span className="grp-count">{count} {count === 1 ? 'match' : 'matches'}</span>
      </button>
      {open && (
        <div className="grp-body">
          {count === 0 && <div className="grp-empty">{empty}</div>}
          {items?.map(item => (
            <ResultRow key={item._id || item.aspireItemId || item.vendorCatalogProductId} item={item} kind={kind} onSelect={onSelect} linking={linking} showPromote={showPromote} />
          ))}
        </div>
      )}
    </div>
  )
}

function ResultRow ({ item, kind, onSelect, linking, showPromote }) {
  const [open, setOpen] = React.useState(false)
  const hasDetails = !!(item.imageUrl || item.details || item.externalUrl)
  const isActive = linking === (item._id || item.aspireItemId || item.vendorCatalogProductId)
  const priceHint = item.vendorPriceHints?.[0]
  const displayPrice = priceHint ? priceHint.unitCost : item.bestPrice
  const [imgErr, setImgErr] = React.useState(false)

  return (
    <div className={`res-row-wrap${open ? ' open' : ''}`}>
      <div className="res-row" style={{ opacity: linking && !isActive ? 0.5 : 1 }}>
        {hasDetails
          ? <button className="res-tw" onClick={() => setOpen(!open)}>{open ? '▾' : '▸'}</button>
          : <span className="res-tw res-tw--empty" />
        }
        {item.imageUrl && !imgErr && (
          <img src={item.imageUrl} alt="" className="res-thumb" onError={() => setImgErr(true)} />
        )}
        <div className={`res-main${hasDetails ? ' clickable' : ''}`} onClick={() => hasDetails && setOpen(!open)}>
          <div className="res-name">
            {item.canonicalName}
            {kind === 'vendor' && priceHint && <span className="res-vendor"> · {priceHint.vendor}</span>}
            {item.externalUrl && (
              <a href={item.externalUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 11, color: 'var(--ink-3)', textDecoration: 'none' }} title="View on vendor site">↗</a>
            )}
          </div>
          <div className="res-meta">
            {item.category && <><span>{cap(item.category)}</span><span className="dot-sep">·</span></>}
            <span className="mono">{item.unit}</span>
            {displayPrice != null && <><span className="dot-sep">·</span><span className="mono">{fmt$(displayPrice)}</span></>}
            {priceHint && <><span className="dot-sep">·</span><span>{priceHint.vendor}</span></>}
            {hasDetails && !open && (
              <><span className="dot-sep">·</span><button className="res-more" onClick={e => { e.stopPropagation(); setOpen(true) }}>Show details</button></>
            )}
          </div>
        </div>
        <div className="res-action">
          {isActive && <CircularProgress size={14} />}
          {!isActive && (
            <button
              className="hmi-btn hmi-btn-primary hmi-btn-sm"
              disabled={!!linking}
              onClick={() => onSelect(item)}
            >
              {showPromote ? 'Promote + link →' : 'Link →'}
            </button>
          )}
        </div>
      </div>
      {open && <ResultDetails item={item} kind={kind} />}
    </div>
  )
}

function ResultDetails ({ item, kind }) {
  const [imgErr, setImgErr] = React.useState(false)
  const d = item.details || {}
  const pricing = d.pricing

  return (
    <div className="res-det">
      <div className="res-det-grid">
        <div className="res-det-img">
          {item.imageUrl && !imgErr
            ? <img src={item.imageUrl} alt="" onError={() => setImgErr(true)} />
            : <StripedPlaceholder size={100} label="product image" />
          }
        </div>
        <div className="res-det-body">
          {pricing && (
            <div className="pricing-tiers">
              {pricing.our != null && <PriceTier label="Our price" value={pricing.our} primary />}
              {pricing.account != null && <PriceTier label="Account" value={pricing.account} />}
              {pricing.list != null && <PriceTier label="List" value={pricing.list} />}
              {pricing.quote != null && <PriceTier label="Quoted" value={pricing.quote} dim />}
            </div>
          )}

          <div className="res-det-fields">
            {kind === 'vendor' && item.vendorPriceHints?.[0]?.vendor && <DetField label="Vendor" value={item.vendorPriceHints[0].vendor} />}
            {(item.vendorSku || d.mpn) && <DetField label={kind === 'vendor' ? 'Vendor SKU' : 'Catalog ID'} value={item.vendorSku || d.mpn} mono />}
            {d.mfg && <DetField label="Manufacturer" value={d.mfg} />}
            {d.mpn && <DetField label="MPN" value={d.mpn} mono />}
            {d.category && <DetField label="Category" value={d.category} />}
            {d.catalogVersion && <DetField label="Catalog version" value={d.catalogVersion} mono />}
          </div>

          {d.description && (
            <div className="res-det-desc">
              <span className="dlg-overline">Description</span>
              <p>{d.description}</p>
            </div>
          )}

          {d.history?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <span className="dlg-overline">Recent purchase history · {d.history.length}</span>
              <table className="hist-table">
                <thead>
                  <tr><th>Date</th><th>Vendor</th><th>Project</th><th>Qty</th><th>Unit price</th></tr>
                </thead>
                <tbody>
                  {d.history.map((h, i) => (
                    <tr key={i}>
                      <td className="mono">{h.date}</td>
                      <td>{h.vendor}</td>
                      <td className="muted">{h.project}</td>
                      <td className="mono">{h.qty}</td>
                      <td className="mono">{fmt$(h.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function CreateNewBlock ({ material, onCreateNew, linking, totalMatches }) {
  const opt = material?.vendorOptions?.[0]
  const [open, setOpen] = React.useState(false)
  const [name, setName] = React.useState(material?.canonicalName || '')
  const [cat, setCat] = React.useState(material?.category || 'general')
  const [unit, setUnit] = React.useState(material?.unit || opt?.uom || 'Each')

  if (!open) {
    return (
      <div className="create-block">
        <div className="create-head">
          <span className="create-icon">+</span>
          <div className="create-text">
            <div className="create-title">Don't see a match?</div>
            <div className="create-sub">
              {totalMatches ? `None of the ${totalMatches} results above are right?` : 'No catalog has this material yet?'}
              {' '}Create a new item in our Material Catalog.
            </div>
          </div>
          <button className="hmi-btn hmi-btn-outline hmi-btn-sm" onClick={() => setOpen(true)}>Create new material →</button>
        </div>
      </div>
    )
  }

  return (
    <div className="create-block" style={{ borderStyle: 'solid', borderColor: 'var(--primary-soft)' }}>
      <span className="dlg-overline">New material catalog item</span>
      <div className="create-form">
        <TextField size="small" label="Canonical name" variant="outlined" value={name} onChange={e => setName(e.target.value)} style={{ flex: 2, minWidth: 200 }} />
        <TextField select size="small" label="Category" variant="outlined" value={cat} onChange={e => setCat(e.target.value)} SelectProps={{ native: true }} style={{ flex: 1, minWidth: 120 }}>
          {CATEGORIES.map(c => <option key={c} value={c}>{cap(c)}</option>)}
        </TextField>
        <TextField size="small" label="Unit" variant="outlined" value={unit} onChange={e => setUnit(e.target.value)} style={{ width: 90 }} />
      </div>
      {opt && (
        <div className="create-vendor">
          <span className="dlg-overline">First vendor option (from this material)</span>
          <div className="create-vendor-card">
            <SourceChip kind="vendor" size="xs" />
            <b>{opt.vendor}</b>
            <span className="mono">{opt.vendorSku}</span>
            <span className="mono">{fmt$(opt.unitPrice)}/{opt.uom || material.unit}</span>
          </div>
        </div>
      )}
      <div className="create-actions">
        <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={() => setOpen(false)}>Back</button>
        <button
          className="hmi-btn hmi-btn-primary hmi-btn-sm"
          disabled={!!linking || !name.trim()}
          onClick={() => onCreateNew({ canonicalName: name.trim(), category: cat, unit, vendor: opt?.vendor, unitPrice: opt?.unitPrice, vendorSku: opt?.vendorSku })}
        >
          {linking === 'create-new' ? 'Creating...' : 'Create + link →'}
        </button>
      </div>
    </div>
  )
}

const CatalogLinkDialog = ({ material: initialMaterial, materialId: initialMaterialId, projectId, url, onLinked, onClose, queue, queueIndex: initialQueueIndex, onSkipMaterial }) => {
  const [currentIdx, setCurrentIdx] = React.useState(initialQueueIndex >= 0 ? initialQueueIndex : 0)
  const isWizard = queue && queue.length > 1
  const activeMaterial = isWizard ? (queue[currentIdx] || initialMaterial) : initialMaterial
  const activeMaterialId = isWizard ? (activeMaterial?._id || initialMaterialId) : initialMaterialId

  const [search, setSearch] = React.useState(activeMaterial?.canonicalName || '')
  const [results, setResults] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [linking, setLinking] = React.useState(null)
  const [error, setError] = React.useState(null)
  const [catalogProduct, setCatalogProduct] = React.useState(null)
  const [imgError, setImgError] = React.useState(false)
  const [linkedIds, setLinkedIds] = React.useState(new Set())
  const [skippedIds, setSkippedIds] = React.useState(new Set())

  const opt = activeMaterial?.vendorOptions?.[0]

  const resetForMaterial = React.useCallback((m) => {
    setSearch(m?.canonicalName || '')
    setResults([])
    setError(null)
    setLinking(null)
    setCatalogProduct(null)
    setImgError(false)
  }, [])

  React.useEffect(() => {
    resetForMaterial(activeMaterial)
  }, [currentIdx])

  React.useEffect(() => {
    if (!opt?.vendorSku || !url) return
    setCatalogProduct(null)
    setImgError(false)
    axios.get(`${url}/api/vendor-catalogs/products/search`, { params: { q: opt.vendorSku, limit: 1 } })
      .then(res => {
        const found = Array.isArray(res.data?.results) ? res.data.results : []
        const match = found.find(p =>
          String(p.vendorItemNumber || '').replace(/\s/g, '').toLowerCase() === String(opt.vendorSku).replace(/\s/g, '').toLowerCase()
        )
        if (match) setCatalogProduct(match)
      })
      .catch(() => {})
  }, [opt?.vendorSku, url, currentIdx])

  React.useEffect(() => {
    if (!search.trim() || search.trim().length < 2) { setResults([]); return }
    const timer = setTimeout(() => {
      setLoading(true)
      axios.get(`${url}/api/bid-estimation/unified-catalog`, { params: { search: search.trim(), limit: 30 } })
        .then(res => { setResults(Array.isArray(res.data) ? res.data : []); setLoading(false) })
        .catch(() => { setResults([]); setLoading(false) })
    }, 300)
    return () => clearTimeout(timer)
  }, [search, url])

  const advanceToNext = React.useCallback(() => {
    if (!isWizard) return
    for (let i = currentIdx + 1; i < queue.length; i++) {
      const id = queue[i]?._id
      if (!linkedIds.has(id) && !skippedIds.has(id)) {
        setCurrentIdx(i)
        return
      }
    }
    onLinked()
  }, [isWizard, currentIdx, queue, linkedIds, skippedIds, onLinked])

  const handleSelect = (item) => {
    const id = item._id || item.aspireItemId || item.vendorCatalogProductId
    setLinking(id)
    setError(null)
    const body = buildLinkBody(item)
    if (opt) {
      body.vendorEnrichment = {
        vendor: opt.vendor,
        vendorSku: opt.vendorSku,
        unitPrice: opt.unitPrice,
        uom: opt.uom
      }
    }
    axios.put(`${url}/api/bid-estimation/projects/${projectId}/materials/${activeMaterialId}/promote-and-link`, body)
      .then(() => {
        setLinking(null)
        if (isWizard) {
          setLinkedIds(prev => new Set(prev).add(activeMaterialId))
          advanceToNext()
        } else {
          onLinked()
        }
      })
      .catch(err => { setLinking(null); setError(err.response?.data?.error || err.message) })
  }

  const [duplicates, setDuplicates] = React.useState(null)

  const handleCreateNew = (data) => {
    setLinking('create-new')
    setError(null)
    setDuplicates(null)
    axios.put(`${url}/api/bid-estimation/projects/${projectId}/materials/${activeMaterialId}/promote-and-link`, { createNew: data })
      .then(() => {
        setLinking(null)
        if (isWizard) {
          setLinkedIds(prev => new Set(prev).add(activeMaterialId))
          advanceToNext()
        } else {
          onLinked()
        }
      })
      .catch(err => {
        setLinking(null)
        if (err.response?.status === 409 && err.response?.data?.duplicates) {
          setDuplicates({ items: err.response.data.duplicates, originalData: data })
        } else {
          setError(err.response?.data?.error || err.message)
        }
      })
  }

  const handleForceCreate = () => {
    if (!duplicates?.originalData) return
    setLinking('create-new')
    setDuplicates(null)
    axios.put(`${url}/api/bid-estimation/projects/${projectId}/materials/${activeMaterialId}/promote-and-link`, { createNew: { ...duplicates.originalData, force: true } })
      .then(() => {
        setLinking(null)
        if (isWizard) {
          setLinkedIds(prev => new Set(prev).add(activeMaterialId))
          advanceToNext()
        } else {
          onLinked()
        }
      })
      .catch(err => { setLinking(null); setError(err.response?.data?.error || err.message) })
  }

  const handleWizardSkip = () => {
    if (onSkipMaterial) onSkipMaterial(activeMaterialId)
    setSkippedIds(prev => new Set(prev).add(activeMaterialId))
    advanceToNext()
  }

  const goToPrev = () => {
    for (let i = currentIdx - 1; i >= 0; i--) {
      const id = queue[i]?._id
      if (!linkedIds.has(id)) {
        setCurrentIdx(i)
        return
      }
    }
  }

  const goToNext = () => {
    for (let i = currentIdx + 1; i < queue.length; i++) {
      const id = queue[i]?._id
      if (!linkedIds.has(id) && !skippedIds.has(id)) {
        setCurrentIdx(i)
        return
      }
    }
  }

  const remaining = isWizard ? queue.filter(m => !linkedIds.has(m._id) && !skippedIds.has(m._id)).length : 0
  const hasPrev = isWizard && queue.slice(0, currentIdx).some(m => !linkedIds.has(m._id))
  const hasNext = isWizard && queue.slice(currentIdx + 1).some(m => !linkedIds.has(m._id) && !skippedIds.has(m._id))

  const aspire = results.filter(r => r.source === 'aspire')
  const catalog = results.filter(r => r.source === 'catalog')
  const vendor = results.filter(r => r.source !== 'catalog' && r.source !== 'aspire')
  const totalMatches = results.length

  const sourceVendor = opt?.vendor?.toLowerCase() || ''
  const hasKnownVendorProduct = !!catalogProduct
  const vendorDefaultOpen = !hasKnownVendorProduct && vendor.length > 0

  React.useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose()
      if (!isWizard || linking) return
      if (e.key === 'ArrowLeft' && hasPrev) { e.preventDefault(); goToPrev() }
      if (e.key === 'ArrowRight' && hasNext) { e.preventDefault(); goToNext() }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose, isWizard, linking, hasPrev, hasNext, currentIdx])

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: 16, maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogHeader material={activeMaterial} catalogProduct={catalogProduct} imgError={imgError} setImgError={setImgError} onClose={onClose} />

      <div className="dlg-search" style={{ flexShrink: 0 }}>
        <span className="dlg-overline">Search</span>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search across all catalogs at once..."
          value={search}
          onChange={e => { setSearch(e.target.value); setError(null) }}
          variant="outlined"
          style={{ marginTop: 4 }}
        />
        <div className="dlg-search-hint">Searching across all catalogs at once.</div>
      </div>

      {error && <div style={{ color: 'var(--risk)', fontSize: 12, padding: '0 24px 8px', flexShrink: 0 }}>{error}</div>}

      <DialogContent style={{ padding: 0, overflow: 'auto' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 20 }}><CircularProgress size={20} /></div>
        )}

        {!loading && search.trim().length >= 2 && (
          <>
            <Group kind="catalog" title="Our Material Catalog" subtitle="Best match — Aspire item linked, vendor pricing" items={catalog} onSelect={handleSelect} linking={linking}
              defaultOpen={catalog.length > 0}
              empty="No match in our catalog yet. Try Aspire or vendor catalogs below." />
            <Group kind="aspire" title="Aspire Catalog" subtitle="Corporate items — selecting promotes to our Material Catalog" items={aspire} onSelect={handleSelect} linking={linking} showPromote
              empty="No Aspire item matches — most landscape line items aren't in the corporate catalog." />
            <Group kind="vendor" title="Vendor Catalogs"
              subtitle={hasKnownVendorProduct ? `Already identified as ${sourceVendor} product — showing other vendor matches` : 'Ewing live, Aggregate Dashboard, AZ Wholesale Growers'}
              items={vendor} onSelect={handleSelect} linking={linking} showPromote
              defaultOpen={vendorDefaultOpen}
              empty={hasKnownVendorProduct ? `This item is already identified in the ${opt?.vendor} catalog. Link to an Aspire or Material Catalog item above.` : 'No live vendor catalog matches.'} />
            <CreateNewBlock material={activeMaterial} onCreateNew={handleCreateNew} linking={linking} totalMatches={totalMatches} />

            {duplicates && (
              <div className="create-block" style={{ borderStyle: 'solid', borderColor: 'var(--risk)', background: 'var(--bg-risk-soft, rgba(211,47,47,0.04))' }}>
                <span className="dlg-overline" style={{ color: 'var(--risk)' }}>Similar items already exist</span>
                <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 8 }}>
                  Link to one of these instead, or create anyway if this is truly a new item.
                </div>
                {duplicates.items.map(d => (
                  <div key={d._id} className="res-row" style={{ padding: '6px 0', borderBottom: '1px solid var(--line-1)' }}>
                    <div className="res-main" style={{ flex: 1 }}>
                      <div className="res-name">{d.canonicalName}</div>
                      <div className="res-meta">
                        <span>{cap(d.category)}</span>
                        <span className="dot-sep">·</span>
                        <span className="mono">{d.unit}</span>
                        {d.bestPrice != null && <><span className="dot-sep">·</span><span className="mono">{fmt$(d.bestPrice)}</span></>}
                      </div>
                    </div>
                    <button className="hmi-btn hmi-btn-primary hmi-btn-sm" onClick={() => handleSelect({ source: 'catalog', materialCatalogItemId: d._id, _id: d._id })}>
                      Link to this →
                    </button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                  <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={() => setDuplicates(null)}>Cancel</button>
                  <button className="hmi-btn hmi-btn-outline hmi-btn-sm" onClick={handleForceCreate} disabled={!!linking}>
                    {linking === 'create-new' ? 'Creating...' : 'Create anyway →'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>

      <div className="dlg-foot">
        {isWizard ? (
          <>
            <div className="dlg-foot-nav">
              <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={goToPrev} disabled={!hasPrev || !!linking}>← Prev</button>
              <span className="dlg-foot-pos">
                {currentIdx + 1} of {queue.length}
                {(linkedIds.size > 0 || skippedIds.size > 0) && (
                  <span style={{ color: 'var(--ink-4)', marginLeft: 6 }}>
                    ({linkedIds.size} linked{skippedIds.size > 0 ? `, ${skippedIds.size} skipped` : ''}, {remaining} left)
                  </span>
                )}
              </span>
              <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={goToNext} disabled={!hasNext || !!linking}>Next →</button>
            </div>
            <div className="dlg-foot-actions">
              <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={handleWizardSkip} disabled={!!linking}>Skip this</button>
              <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <span className="dlg-foot-help">
              <span className="dlg-kbd">↑↓</span> navigate &nbsp; <span className="dlg-kbd">↵</span> link &nbsp; <span className="dlg-kbd">esc</span> close
            </span>
            <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={onClose}>Cancel</button>
          </>
        )}
      </div>
    </Dialog>
  )
}

export default CatalogLinkDialog
